/*
  Settings: lesson reminders (the heart of the app), notifications on this device, account and
  privacy. The reminder section is adapted from Tazkiyah's Reminders screen: the same permission
  steps (Android "Alarms & reminders" and full-screen approval), test alarm, and warnings.
*/
import { Segmented, Switch, message } from "antd";
import Avatar from "app/components/Icons/Avatar";
import { TopBar } from "app/components/Shell";
import { Button } from "app/components/v1/Button";
import { applyConsent, getConsent, type Consent } from "app/components/ConsentBanner";
import { isApple, isNativeApp, isStandalone, nativePlatform, STORE_LINKS } from "app/native/nativeApp";
import { getPhoneReminderStatus, openAndroidAlarmSettings, phoneCanRing, previewAlarm, requestPhoneReminderPermissions, stopAlarmPreview, type PhoneReminderStatus } from "app/native/nativeReminders";
import { currentWebSubscription, pushSupport, registerPhonePush, registerWebPush, syncDeviceReminders, unregisterWebPush } from "app/native/push";
import { useMeQuery } from "app/services/account";
import { useGetPushConfigQuery, useTestDeviceMutation } from "app/services/devices";
import { clearSession } from "app/services/index";
import type { ReminderPrefs } from "app/services/types";
import { MAIN_SELECTORS, remindersSlice } from "app/store";
import storage, { STORAGE_KEYS } from "app/utils/storage";
import { timeZoneName } from "app/utils/time";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const MINUTES = [0, 5, 10, 15, 30, 60] as const;

function Row({ label, hint, children }: { label: string; hint?: string; children?: React.ReactNode }) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-3 py-3" style={{ borderBottom: "1px solid #f1f1f5" }}>
      <div><div style={{ fontWeight: 600, color: "#171725" }}>{label}</div>{hint && <div className="ct-muted" style={{ fontSize: 12 }}>{hint}</div>}</div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

export default function Settings() {
  const dispatch = useDispatch();
  const prefs = useSelector(MAIN_SELECTORS.reminders);
  const { profile } = useSelector(MAIN_SELECTORS.user);
  const { data: me } = useMeQuery();
  const { data: pushConfig } = useGetPushConfigQuery();
  const [testDevice] = useTestDeviceMutation();
  const [status, setStatus] = useState<PhoneReminderStatus | null>(null);
  const [webOn, setWebOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [consent, setConsent] = useState<Consent>(getConsent());
  const support = pushSupport();
  const native = isNativeApp();

  const refreshStatus = useCallback(async () => {
    setStatus(await getPhoneReminderStatus());
    setWebOn(Boolean(await currentWebSubscription()));
  }, []);
  useEffect(() => {
    void refreshStatus();
    const onVisible = () => { if (document.visibilityState === "visible") void refreshStatus(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshStatus]);

  const update = (patch: Partial<ReminderPrefs>) => {
    const next = { ...prefs, ...patch };
    dispatch(remindersSlice.actions.setPrefs(next));
    void syncDeviceReminders(next);
  };

  const turnOnPhone = async () => {
    setBusy(true);
    try {
      storage.setItem(STORAGE_KEYS.PHONE_ASKED, "1");
      const s = await requestPhoneReminderPermissions(prefs.mode !== "notification");
      setStatus(s);
      if (pushConfig?.phonePush) await registerPhonePush(prefs);
      if (s?.platform === "android" && prefs.mode !== "notification" && !s.exactAlarms) message.info("One more step: switch on \"Allow setting alarms and reminders\" for ClassTutor.");
      else message.success("Lesson alarms are on for this phone.");
    } finally { setBusy(false); }
  };

  const turnOnWeb = async () => {
    if (!pushConfig?.vapidPublicKey) { message.error("Notifications are not set up on the server yet."); return; }
    setBusy(true);
    try {
      storage.setItem(STORAGE_KEYS.PHONE_ASKED, "1");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { message.warning("Notifications were not allowed."); return; }
      await registerWebPush(pushConfig.vapidPublicKey, prefs);
      setWebOn(true);
      message.success("Notifications are on for this device.");
    } catch (e) { message.error((e as Error).message || "This device could not be set up."); }
    finally { setBusy(false); }
  };

  const turnOffWeb = async () => { setBusy(true); try { await unregisterWebPush(); setWebOn(false); } finally { setBusy(false); } };

  const sendTest = async () => {
    const sub = await currentWebSubscription();
    if (!sub) return;
    const r = await testDevice({ endpoint: sub.endpoint }).unwrap().catch(() => ({ outcome: "failed" }));
    message[r.outcome === "delivered" ? "success" : "warning"](r.outcome === "delivered" ? "Sent. It should appear in a moment." : "The test could not be sent to this device.");
  };

  const preview = async () => {
    if (previewing) { await stopAlarmPreview(); setPreviewing(false); return; }
    if (await previewAlarm(prefs.sound, prefs.vibrate)) { setPreviewing(true); setTimeout(() => setPreviewing(false), 60000); }
    else message.info("iPhones can't play an alarm in advance. The real one uses your iPhone's alarm sound.");
  };

  const signOut = () => { clearSession(); };

  return (
    <div className="ct-page">
      <TopBar title="Settings" />

      {/* ---- Lesson reminders ---- */}
      <section className="ct-card p-3 mb-3">
        <h2 className="ct-section-title" style={{ fontSize: 15 }}>Lesson reminders</h2>
        <Row label="Remind me before each lesson"><Switch checked={prefs.enabled} onChange={(v) => update({ enabled: v })} /></Row>
        {prefs.enabled && (
          <>
            <div className="py-3" style={{ borderBottom: "1px solid #f1f1f5" }}>
              <div style={{ fontWeight: 600, color: "#171725" }} className="mb-2">How long before</div>
              <Segmented block value={prefs.minutesBefore} onChange={(v) => update({ minutesBefore: v as ReminderPrefs["minutesBefore"] })} options={MINUTES.map((m) => ({ label: m === 0 ? "Now" : m === 60 ? "1 hr" : `${m}m`, value: m }))} />
              <div className="ct-muted mt-1" style={{ fontSize: 12 }}>{prefs.minutesBefore === 0 ? "When the lesson starts." : `${prefs.minutesBefore} minutes before the lesson starts.`}</div>
            </div>
            <div className="py-3" style={{ borderBottom: "1px solid #f1f1f5" }}>
              <div style={{ fontWeight: 600, color: "#171725" }} className="mb-1">How it reminds you</div>
              <Segmented block value={prefs.mode} onChange={(v) => update({ mode: v as ReminderPrefs["mode"] })} options={[{ label: "Notify", value: "notification" }, { label: "Alarm", value: "alarm" }, { label: "Both", value: "both" }]} />
              <div className="ct-muted mt-2" style={{ fontSize: 12 }}>
                {phoneCanRing() ? "An alarm rings loudly and fills the screen, even on silent, until you dismiss it." : native ? "Loud alarms need iOS 26 or later; reminders come as notifications with sound." : "Loud full-screen alarms need the ClassTutor app. On the web you get a notification, and the alarm screen when the app is open."}
              </div>
            </div>
            {prefs.mode !== "notification" && (
              <>
                <Row label="Sound"><Switch checked={prefs.sound} onChange={(v) => update({ sound: v })} /></Row>
                <Row label="Vibration"><Switch checked={prefs.vibrate} onChange={(v) => update({ vibrate: v })} /></Row>
                {status?.platform === "android" && <Row label="Hear it" hint="Rings for up to a minute"><Button size="sm" buttonType="outlined" name={previewing ? "Stop" : "Test alarm"} onClick={preview} /></Row>}
              </>
            )}
          </>
        )}
      </section>

      {/* ---- This device ---- */}
      <section className="ct-card p-3 mb-3">
        <h2 className="ct-section-title" style={{ fontSize: 15 }}>This {native ? (nativePlatform() === "ios" ? "iPhone" : "phone") : "device"}</h2>
        {native ? (
          <>
            <Row label="Notifications" hint={status ? (status.notifications === "granted" ? "Allowed" : "Not allowed yet") : "Checking"}>
              {status?.notifications !== "granted" && <Button size="sm" name="Allow" loading={busy} onClick={turnOnPhone} />}
            </Row>
            {status?.platform === "android" && prefs.mode !== "notification" && (
              <>
                <Row label="Alarms & reminders" hint={status.exactAlarms ? "Approved: alarms ring on the minute" : "Approve so alarms ring on the exact minute"}>
                  {!status.exactAlarms && <Button size="sm" name="Approve" onClick={() => openAndroidAlarmSettings("exact")} />}
                </Row>
                <Row label="Full-screen alarms" hint={status.fullScreen ? "Allowed: alarms show on the lock screen" : "Allow so alarms show when the phone is locked"}>
                  {!status.fullScreen && <Button size="sm" name="Allow" onClick={() => openAndroidAlarmSettings("fullScreen")} />}
                </Row>
              </>
            )}
            {status?.platform === "ios" && status.alarms !== "unavailable" && prefs.mode !== "notification" && (
              <Row label="Alarms" hint={status.alarms === "granted" ? "Allowed" : "Allow alarms so they can ring"}>{status.alarms !== "granted" && <Button size="sm" name="Allow" loading={busy} onClick={turnOnPhone} />}</Row>
            )}
            <Row label="Alarms booked" hint="Re-planned every time the app opens">{status?.booked ?? 0}</Row>
            {!pushConfig?.phonePush && <div className="ct-muted mt-2" style={{ fontSize: 12 }}>New-message alerts for the phone app are being switched on. Until then, open the app to see replies.</div>}
          </>
        ) : support === "ready" ? (
          <>
            <Row label="Notifications" hint={webOn ? "On for this device" : "Hear about replies and lessons"}>
              {webOn ? <Button size="sm" buttonType="outlined" color="secondary" name="Turn off" loading={busy} onClick={turnOffWeb} /> : <Button size="sm" name="Turn on" loading={busy} onClick={turnOnWeb} />}
            </Row>
            {webOn && <Row label="Send a test" hint="Check it reaches this device"><Button size="sm" buttonType="outlined" name="Send" onClick={sendTest} /></Row>}
          </>
        ) : support === "install-first" ? (
          <div className="py-2 ct-muted" style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "#171725" }}>Add ClassTutor to your Home Screen first</div>
            {isApple() ? "Tap the Share button in Safari, then \"Add to Home Screen\". Open it from there to turn on notifications." : "Use your browser's menu and choose \"Add to Home screen\"."}
          </div>
        ) : support === "blocked" ? (
          <div className="py-2 ct-muted" style={{ fontSize: 13 }}>Notifications are blocked for this site. Allow them in your browser's site settings, then come back here.</div>
        ) : (
          <div className="py-2 ct-muted" style={{ fontSize: 13 }}>This browser can't show notifications. Install the ClassTutor app for alarms and alerts.</div>
        )}
        {!native && (
          <div className="mt-3 p-3 rounded-3" style={{ background: "#eceefa" }}>
            <div style={{ fontWeight: 700, color: "#4c54a0" }}>Get the ClassTutor app</div>
            <div className="ct-muted" style={{ fontSize: 13 }}>Loud full-screen lesson alarms that ring even on silent.</div>
            <div className="d-flex gap-2 mt-2">
              {STORE_LINKS.android ? <a className="btn btn-sm btn-primary" href={STORE_LINKS.android}>Google Play</a> : <span className="badge bg-primary">Android: coming soon</span>}
              {STORE_LINKS.ios ? <a className="btn btn-sm btn-primary" href={STORE_LINKS.ios}>App Store</a> : <span className="badge bg-primary">iPhone: coming soon</span>}
            </div>
            {!isStandalone() && <div className="ct-muted mt-2" style={{ fontSize: 12 }}>Meanwhile, add this page to your Home Screen for quick access.</div>}
          </div>
        )}
        <div className="ct-muted mt-3" style={{ fontSize: 12 }}>Times are shown in {timeZoneName().replace("_", " ")}.</div>
      </section>

      {/* ---- Account ---- */}
      <section className="ct-card p-3 mb-3">
        <h2 className="ct-section-title" style={{ fontSize: 15 }}>Account</h2>
        <div className="d-flex align-items-center gap-3 py-2">
          <Avatar name={profile?.avatar} fullName={`${profile?.name ?? ""} ${profile?.lastName ?? ""}`} size={48} />
          <div>
            <div style={{ fontWeight: 700, color: "#171725" }}>{profile?.name} {profile?.lastName}</div>
            <div className="ct-muted" style={{ fontSize: 13 }}>Parent ID: {profile?.parentId}{profile?.email ? ` · ${profile.email}` : ""}</div>
          </div>
        </div>
        {(me?.children ?? []).map((c) => (
          <Row key={c.id} label={`${c.name} ${c.lastName}`} hint={[c.year, c.studentId].filter(Boolean).join(" · ")}><Avatar name={c.avatar} fullName={`${c.name} ${c.lastName}`} size={32} /></Row>
        ))}
        <div className="ct-muted mt-2" style={{ fontSize: 12 }}>To change your details or password, use the <a href={me?.portalUrl || "https://portal.classtutor.co.uk"} target="_blank" rel="noreferrer">ClassTutor portal</a>.</div>
        <Button className="w-100 mt-3" buttonType="outlined" color="danger" name="Sign out" onClick={signOut} />
      </section>

      {/* ---- Privacy ---- */}
      <section className="ct-card p-3 mb-3">
        <h2 className="ct-section-title" style={{ fontSize: 15 }}>Privacy</h2>
        {process.env.REACT_APP_MS_CLARITY ? (
          <Row label="Help improve the app" hint="Record how screens are used (never what you type)"><Switch checked={consent === "yes"} onChange={(v) => { applyConsent(v ? "yes" : "no"); setConsent(v ? "yes" : "no"); }} /></Row>
        ) : null}
        <div className="ct-muted mt-2" style={{ fontSize: 12 }}>
          <a href="https://classtutor.co.uk/privacy-policy" target="_blank" rel="noreferrer">Privacy policy</a> · ClassTutor Ltd · App version {process.env.REACT_APP_VERSION || "dev"}
        </div>
      </section>
    </div>
  );
}
